/**
 * @module lib/appserver/request
 * Utilities for communicating with appserver
 * @see {@link module:state/fetchSagas} for using these APIs in sagas
 */

import { jspCmdRequest, apiRequest } from "lib/ocean";
import queryString from "query-string";

import UnexpectedAPIResponseFormatError from "lib/appserver/responseExceptions/UnexpectedAPIResponseFormatError";
import UnrecognisedResponseCodeError from "lib/appserver/responseExceptions/UnrecognisedResponseCodeError";
import RemoteServerCompleteFailureError from "lib/appserver/responseExceptions/RemoteServerCompleteFailureError";
import UnknownDeviceError from "lib/appserver/responseExceptions/UnknownDeviceError";

// re-export exception classes for convenience
export {
  UnexpectedAPIResponseFormatError,
  UnrecognisedResponseCodeError,
  RemoteServerCompleteFailureError,
  UnknownDeviceError
};

// In order to get appReferrer data from store, lib needs exposure to Redux and saga which is time consuming
// So it is easier to get it from the url args passed by the native app
const APP_REFERRER = queryString.parse(window.location.search).app_referrer;
const HOSTNAME = APP_REFERRER === process.env.REACT_APP_XPRESSIONS_APP_REFERRER ? process.env.REACT_APP_EDUSPOT_LOCAL_HOSTNAME : process.env.REACT_APP_TEACHERS2PARENTS_LOCAL_HOSTNAME;
const ERROR_API_ENDPOINT = `https://${HOSTNAME}/public/appserver/api/log/error.api`;

/**
 * Generic appserver request.
 * This is where our error translation lives.
 * We check the JSend response for problems and throw exceptions.
 * NOTE: these exceptions are thrown inside the promise, so they
 *   can be caught using promise.catch().
 */
export function request(jspCmd, jspArgs, requestOptions) {
  return jspCmdRequest(HOSTNAME, jspCmd, jspArgs, requestOptions)
    .then(response => {

      // check response looks like JSend
      const responseData = response.data;
      if (!responseData.hasOwnProperty("status")) {
        // wtf is this format!?
        throw new UnexpectedAPIResponseFormatError(response);
      }

      // check for error response
      if (responseData.status !== "success") {
        // we can recognise which some errors which may have special handling
        const responseCode = responseData.code;
        switch (responseCode) {
          case UnknownDeviceError.responseCode:
            throw new UnknownDeviceError(response);
          case RemoteServerCompleteFailureError.responseCode:
            throw new RemoteServerCompleteFailureError(response);
          default:
            // unrecognised code - generic exception
            throw new UnrecognisedResponseCodeError(response);
        }
      }

      // nothing thrown - pass through
      return response;

    });
}

/**
 * getAvailableProducts - getAvailableProducts/GetMyApps API
 *
 * @param  {String} mobileNumber
 * @param  {String} deviceToken
 * @returns {Promise}
 */
export function availableProductsRequest(mobileNumber, deviceToken) {
  return request("GetMyApps()", {
    mobile_number: mobileNumber,
    device_token: deviceToken
  });
}

/**
 * dataRequest - ServiceDataRequest() API
 *
 * @param  {String} mobileNumber
 * @param  {String} deviceToken
 * @param  {String} application  "teachers"/ "school_money"
 * @param  {Object} args = {}    dataRequest arguments
 * @returns {Promise}
 */
export function dataRequest(mobileNumber, deviceToken, application, args = {}) {
  return request("ServiceDataRequest()", {
    ...args,
    // Override userIdentity (so there's no way to override in args)
    application: application,
    mobile_number: mobileNumber,
    device_token: deviceToken
  });
}

/**
 * errorReport - send an error report to appserver's error logging
 *   endpoint, which will then be reported to BigBrother.
 *
 * @param {Object} details key-value pairs of error details
 * -- error details --
 * @param {string} details.type e.g. "UnknownDeviceError"
 * @param {string} details.message e.g. "This device was not recognised by appserver.""
 * @param {string} [details.payload] any additional info to log
 * @param {string} [details.stacktrace]
 * -- environment details --
 * @param {string} details.native_version native client version number
 * @param {string} details.web_version web client version number
 * @param {string} details.os device operating system name
 * @param {string} details.os_version device operating system version
 * @param {string} details.device_type
 */
export function errorReport(details) {
  return apiRequest(ERROR_API_ENDPOINT, details);
}



// WEBPACK FOOTER //
// ./src/lib/appserver/request.js